import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CardMedia,
  Link,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { createUser } from "../firebase";
import logo from "../imgs/logo192.png";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const Register = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [point, setPoint] = useState("");
  const [profession, setProfession] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const register = async () => {
    try {
      if (password !== confirmPassword) {
        throw new Error("Las contraseñas no coinciden.");
      }

      await createUser(
        email,
        password,
        displayName, // Si es usuario pro, pasamos el nombre del negocio
        phone,
        profession,
        point
      );

      // Limpiar el formulario después de un registro exitoso
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setDisplayName("");
      setProfession("");
      setPhone("");
      setPoint("");
      setError("");
      navigate("/home");
    } catch (error) {
      setError(error.message);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const tfSx = {
    mb: 0.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background: "rgba(255,255,255,0.05)",
      "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
      "&:hover fieldset": { borderColor: "rgba(0,212,255,0.35)" },
      "&.Mui-focused fieldset": { borderColor: "#00d4ff", borderWidth: "1px" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.4)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d4ff" },
    "& .MuiInputBase-input": { color: "#fff" },
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#080b14", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", position: "relative", overflow: "hidden" }}>
      <Box sx={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)", top: "-200px", right: "-200px", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", width: "500px", height: "500px", borderRadius: "50%", background: "radial-gradient(circle, rgba(123,47,247,0.1) 0%, transparent 70%)", bottom: "-150px", left: "-200px", pointerEvents: "none" }} />
      <Box sx={{
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(32px)",
        WebkitBackdropFilter: "blur(32px)",
        borderRadius: "28px",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 32px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
        p: { xs: 3, sm: 4 },
        width: "100%",
        maxWidth: 420,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        zIndex: 1,
        animation: "fadeInUp 0.5s ease",
      }}>
        <Box sx={{ width: 64, height: 64, mb: 1.5, borderRadius: "18px", background: "linear-gradient(135deg, rgba(0,212,255,0.15), rgba(123,47,247,0.15))", border: "1px solid rgba(0,212,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 28px rgba(0,212,255,0.1)", overflow: "hidden" }}>
          <CardMedia component="img" image={logo} alt="FindMe logo" sx={{ width: 46, height: 46, objectFit: "contain" }} />
        </Box>
        <Typography variant="h5" fontWeight={800} mb={0.3} sx={{ background: "linear-gradient(135deg, #00d4ff, #7b2ff7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
          Crear cuenta
        </Typography>
        <Typography variant="body2" mb={2.5} sx={{ color: "rgba(255,255,255,0.35)" }}>
          Únete a FindMe hoy
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2, width: "100%", borderRadius: 2, bgcolor: "rgba(211,47,47,0.1)", border: "1px solid rgba(211,47,47,0.3)", color: "#ff6b7a" }}>{error}</Alert>
        )}
        <Box component="form" sx={{ width: "100%" }}>
          <TextField label="Correo Electrónico" fullWidth margin="dense" required value={email} onChange={e => setEmail(e.target.value)} sx={tfSx} />
          <TextField label="Contraseña" variant="outlined" type={showPassword ? "text" : "password"} autoComplete="new-password" value={password} required onChange={e => setPassword(e.target.value)} fullWidth margin="dense" sx={tfSx}
            InputProps={{ endAdornment: (<IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>) }}
          />
          <TextField label="Repetir Contraseña" type="password" fullWidth required margin="dense" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} sx={tfSx}
            InputProps={{ endAdornment: (<IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>) }}
          />
          <TextField label="Profesión" fullWidth margin="dense" required value={profession} onChange={e => setProfession(e.target.value)} sx={tfSx} />
          <TextField label="Nombre" fullWidth margin="dense" required value={displayName} onChange={e => setDisplayName(e.target.value)} sx={tfSx} />
          <TextField label="Teléfono" fullWidth margin="dense" required value={phone} onChange={e => setPhone(e.target.value)} sx={tfSx} />
          <TextField label="Localidad" fullWidth margin="dense" required value={point} onChange={e => setPoint(e.target.value)} sx={tfSx} />
          <Button variant="contained" fullWidth onClick={register} sx={{ mt: 2, background: "linear-gradient(135deg, #00d4ff, #7b2ff7)", color: "#fff", borderRadius: "14px", fontWeight: 700, fontSize: "1rem", py: 1.5, textTransform: "none", letterSpacing: "0.3px", boxShadow: "0 8px 25px rgba(0,212,255,0.2)", "&:hover": { background: "linear-gradient(135deg, #00bfe8, #6a20d6)", boxShadow: "0 12px 35px rgba(0,212,255,0.35)", transform: "translateY(-2px)" }, transition: "all 0.2s ease" }}>
            Registrarse
          </Button>
        </Box>
        <Box sx={{ width: "100%", height: "1px", background: "rgba(255,255,255,0.06)", my: 2 }} />
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.35)" }}>
          ¿Ya tienes cuenta?{' '}
          <Link href="/login" sx={{ color: "#00d4ff", fontWeight: 600, textDecoration: "none", "&:hover": { opacity: 0.8 } }}>
            Iniciar sesión
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default Register;
